import {
    useState, useMemo, useEffect, useCallback, useRef
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import Modal from 'components/Modal/Modal';
import {
    Box, Text, Button, Flex, Link, Icon
} from 'components/ui';
import RichText from 'components/Content/RichText';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import RecapBasketEN from 'components/Content/Recap/RecapBasket/locales/RecapBasket_en_US';
import RecapBasketFR from 'components/Content/Recap/RecapBasket/locales/RecapBasket_fr_CA';
import {
    colors, fontSizes, space, mediaQueries, radii, lineHeights, borders, shadows
} from 'style/config';
import { DebouncedResize } from 'constants/events';

import basketSelector from 'selectors/basket/basketSelector';
import promoErrorSelector from 'selectors/promo/promoError/promoErrorSelector';
import promoUtils from 'utils/Promos';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(RecapBasketEN, RecapBasketFR);

const LEGAL_TEXT_ID = 'rtco-offer-details-legal';

function hasRichTextBody(content) {
    return Boolean(content?.json?.content?.length);
}

/**
 * Offer-details modal for an RTCO promo: shows banner/legal copy and lets the shopper apply or remove the promo.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onDismiss
 * @param {Object} [props.rtcoPromoData]
 * @returns {JSX.Element|null}
 */
function RtcoOfferDetailsModal({ isOpen, onDismiss, rtcoPromoData }) {
    const [isLegalExpanded, setIsLegalExpanded] = useState(false);
    const [isLegalExpandable, setIsLegalExpandable] = useState(false);
    const [applyAttempted, setApplyAttempted] = useState(false);

    const basket = useSelector(basketSelector);
    const promoError = useSelector(promoErrorSelector);
    const legalRef = useRef(null);

    const promoCode = rtcoPromoData?.promoCode;
    const appliedPromos = basket?.appliedPromotions || [];
    const isApplied = Boolean(promoCode) && appliedPromos.some(p => p?.couponCode?.toLowerCase?.() === promoCode.toLowerCase());

    // A failed apply rejects with { errorCode, errorMessages } and utils/Promos.js stores it (tagged
    // with the promoCode) in store.promo.promoError. Scope it to this promo so we never show another
    // button's error, and only after the shopper actually tried to apply.
    const promoApplyError = useMemo(() => {
        if (!promoCode || promoError?.promoCode?.toLowerCase?.() !== promoCode.toLowerCase()) {
            return null;
        }

        return promoError.errorMessages?.length ? promoError.errorMessages.join(' ') : null;
    }, [promoError, promoCode]);

    const disclaimerContent = useMemo(() => {
        const d = rtcoPromoData?.disclaimer?.content;

        return hasRichTextBody(d) ? d : null;
    }, [rtcoPromoData]);

    const measureLegalOverflow = useCallback(() => {
        if (!isLegalExpanded && legalRef.current) {
            setIsLegalExpandable(legalRef.current.scrollHeight > legalRef.current.clientHeight + 1);
        }
    }, [isLegalExpanded]);

    useEffect(() => {
        if (isOpen) {
            setIsLegalExpanded(false);
            setApplyAttempted(false);
        }
    }, [isOpen, rtcoPromoData?.context]);

    // Once the promo actually applies, the attempt succeeded — clear any error we were showing.
    useEffect(() => {
        if (isApplied) {
            setApplyAttempted(false);
        }
    }, [isApplied]);

    // The clamped legal text only changes height across breakpoints (SMUI/LGUI), so re-measure
    // on mount and on resize instead of per frame. Skip entirely while the modal is closed or
    // there is no disclaimer to measure so we don't hold an idle global listener.
    useEffect(() => {
        if (!isOpen || !disclaimerContent) {
            return undefined;
        }

        measureLegalOverflow();
        window.addEventListener(DebouncedResize, measureLegalOverflow);

        return () => window.removeEventListener(DebouncedResize, measureLegalOverflow);
    }, [measureLegalOverflow, disclaimerContent, isOpen]);

    if (!rtcoPromoData) {
        return null;
    }

    const { banner, modalSubtitle, modalEligibility, legalText } = rtcoPromoData;
    const bannerText = banner?.text;

    // promoUtils is fire-and-forget and refreshes the basket itself; a failed apply lands in
    // store.promo.promoError, which we surface inline below once the shopper has tried.
    const handleApplyPromotion = () => {
        if (promoCode) {
            setApplyAttempted(true);
            promoUtils.applyPromo(promoCode);
        }
    };

    const handleRemovePromotion = () => {
        promoCode && promoUtils.removePromo(promoCode);
    };

    const showApplyError = applyAttempted && !isApplied && Boolean(promoApplyError);

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            width={3}
            showDismiss={true}
            hasBodyScroll={true}
            customStyle={styles.modalStyles}
        >
            <Modal.Header>
                <Modal.Title>{getText('offerDetailsTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body
                hasBodyScroll={true}
                css={styles.modalBody}
            >
                {hasRichTextBody(bannerText) && (
                    <Box marginBottom={3}>
                        <RichText
                            content={bannerText}
                            style={styles.headline}
                        />
                    </Box>
                )}
                {modalSubtitle && (
                    <Text
                        is='p'
                        fontSize='sm'
                        marginBottom={3}
                        lineHeight='tight'
                        children={modalSubtitle}
                    />
                )}
                {modalEligibility && (
                    <Text
                        is='p'
                        fontSize='xs'
                        color='gray'
                        marginBottom={legalText ? 2 : 3}
                        lineHeight='tight'
                        css={styles.eligibilityText}
                        children={modalEligibility}
                    />
                )}
                {legalText && (
                    <Text
                        is='p'
                        fontSize='xs'
                        color='gray'
                        marginBottom={3}
                        lineHeight='tight'
                        children={legalText}
                    />
                )}
                {Boolean(disclaimerContent) && (
                    <Box>
                        <Box
                            id={LEGAL_TEXT_ID}
                            ref={legalRef}
                            css={isLegalExpanded ? styles.legalExpanded : styles.legalCollapsed}
                        >
                            <RichText
                                content={disclaimerContent}
                                style={styles.disclaimerRichText}
                            />
                        </Box>
                        {(isLegalExpandable || isLegalExpanded) && (
                            <Link
                                display='inline-block'
                                marginTop={2}
                                marginBottom={2}
                                fontSize='sm'
                                color='blue'
                                underline={true}
                                aria-expanded={isLegalExpanded}
                                aria-controls={LEGAL_TEXT_ID}
                                onClick={() => setIsLegalExpanded(v => !v)}
                                data-at={Sephora.debug.dataAt('rtco_offer_details_read_more')}
                                children={isLegalExpanded ? getText('readLess') : getText('readMore')}
                            />
                        )}
                    </Box>
                )}
                {showApplyError && (
                    <Flex
                        role='alert'
                        alignItems='flex-start'
                        gap={2}
                        marginTop={3}
                        color={colors.error}
                        css={styles.applyError}
                        data-at={Sephora.debug.dataAt('rtco_offer_details_error')}
                    >
                        <Icon
                            name='alert'
                            size={16}
                        />
                        <Text children={promoApplyError} />
                    </Flex>
                )}
            </Modal.Body>
            <Modal.Footer
                css={styles.footer}
                hasShadowBorderForSMUI={true}
            >
                {isApplied ? (
                    <AppliedState onRemove={handleRemovePromotion} />
                ) : (
                    <ApplyButton
                        onApply={handleApplyPromotion}
                        promoCode={promoCode}
                    />
                )}
            </Modal.Footer>
        </Modal>
    );
}

function AppliedState({ onRemove }) {
    return (
        <Box width={['100%', null, 200]}>
            <Box
                width='100%'
                paddingY={2}
                css={styles.appliedBox}
                data-at={Sephora.debug.dataAt('rtco_offer_details_applied')}
            >
                <Flex
                    alignItems='center'
                    justifyContent='center'
                    marginBottom={1}
                    width='100%'
                >
                    <Icon
                        name='checkmark'
                        size={14}
                        marginRight={1}
                    />
                    <Flex flexDirection='column'>
                        <Text
                            is='p'
                            fontWeight='bold'
                            children={getText('applied')}
                        />
                        <Link
                            color='blue'
                            display='block'
                            width='100%'
                            fontSize={fontSizes.sm}
                            fontWeight='normal'
                            css={styles.removeLink}
                            onClick={e => {
                                e.preventDefault();
                                onRemove();
                            }}
                            data-at={Sephora.debug.dataAt('rtco_offer_details_remove')}
                            children={getText('remove')}
                        />
                    </Flex>
                </Flex>
            </Box>
        </Box>
    );
}

AppliedState.propTypes = {
    onRemove: PropTypes.func.isRequired
};

function ApplyButton({ onApply, promoCode }) {
    return (
        <Button
            variant='secondary'
            width={['100%', 140]}
            onClick={onApply}
            disabled={!promoCode}
            data-at={Sephora.debug.dataAt('rtco_offer_details_apply')}
            children={getText('apply')}
        />
    );
}

ApplyButton.propTypes = {
    onApply: PropTypes.func.isRequired,
    promoCode: PropTypes.string
};

const styles = {
    modalStyles: {
        inner: {
            [mediaQueries.sm]: {
                width: '80%',
                borderRadius: radii[2]
            }
        }
    },
    modalBody: {
        paddingBottom: space[2]
    },
    eligibilityText: {
        whiteSpace: 'pre-wrap'
    },
    applyError: {
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight
    },
    appliedBox: {
        border: `${borders[1]} ${colors.black}`,
        borderRadius: radii.full,
        backgroundColor: colors.white,
        textAlign: 'center'
    },
    removeLink: {
        textAlign: 'center'
    },
    headline: {
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        fontWeight: 'bold',
        '& p': {
            marginBottom: 0
        }
    },
    disclaimerRichText: {
        fontSize: fontSizes.xs,
        lineHeight: lineHeights.tight,
        color: colors.gray,
        '& p': {
            marginBottom: space[2]
        },
        '& p:last-child': {
            marginBottom: 0
        }
    },
    legalCollapsed: {
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 4
    },
    legalExpanded: {
        overflow: 'visible',
        display: 'block'
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: space[4],
        backgroundColor: colors.white,
        boxShadow: shadows.light
    }
};

RtcoOfferDetailsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onDismiss: PropTypes.func.isRequired,
    rtcoPromoData: PropTypes.object
};

export default wrapFunctionalComponent(RtcoOfferDetailsModal, 'RtcoOfferDetailsModal');
